import { AvatarUpload } from "@/components/AvatarUpload";
import { Separator } from "@/components/ui/separator";
import { MemberService } from "@/gen/omnios/v1/member_connect.js";
import { useAuthStore } from "@/lib/auth";
import { transport } from "@/lib/grpc";
import { useMembersStore } from "@/lib/members";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/settings/profile")({
	component: ProfileSettingsPage,
});

const memberClient = createClient(MemberService, transport);

function ProfileSettingsPage() {
	const { t } = useTranslation("settings");

	useBreadcrumbs([
		{
			label: t("breadcrumb_settings"),
			href: "/settings",
		},
		{ label: t("profile_breadcrumb") },
	]);

	const { user } = useAuthStore();
	const { members, fetchMembers } = useMembersStore();

	const me = members.find((m) => m.email === user?.email);

	const [avatarUrl, setAvatarUrl] = useState(me?.avatarUrl ?? "");

	const handleAvatarUpload = useCallback(
		async (file: File) => {
			const buf = await file.arrayBuffer();
			const resp = await memberClient.uploadMemberAvatar({
				data: new Uint8Array(buf),
				filename: file.name,
				mimeType: file.type,
			});
			setAvatarUrl(resp.avatarUrl);
			fetchMembers();
		},
		[fetchMembers],
	);

	if (!user) return null;

	const initials =
		user.name
			.split(/\s+/)
			.map((w) => w[0])
			.join("")
			.slice(0, 2)
			.toUpperCase() || "?";

	return (
		<div className="max-w-lg space-y-6">
			<div className="flex items-center gap-4">
				<AvatarUpload
					currentUrl={avatarUrl || me?.avatarUrl}
					initials={initials}
					size={64}
					onUpload={handleAvatarUpload}
				/>
				<div>
					<p className="text-base font-medium">{user.name}</p>
					<p className="text-sm text-muted-foreground">{user.email}</p>
				</div>
			</div>

			<Separator />

			<div className="space-y-4">
				<div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 text-sm">
					<span className="text-muted-foreground">
						{t("profile_label_name")}
					</span>
					<span>{user.name}</span>
					<span className="text-muted-foreground">Email</span>
					<span>{user.email}</span>
					<span className="text-muted-foreground">ID</span>
					<code className="text-xs">{user.id}</code>
				</div>
			</div>

			<Separator />

			<p className="text-xs text-muted-foreground">
				{t("profile_managed_text")}
			</p>
		</div>
	);
}
